import { BandRange, BandVerdict } from '@/components/RatesPage/bands';

/** Степени по эффекту, а не по геометрии: дырка не мягче мёртвой цены (задача 10577). */
export type RateSeverity = 'clean' | 'partial' | 'dead' | 'gap';

export interface SheetGap extends BandRange {
  property: 'performance' | 'quality';
}

export interface SheetModel {
  suppliers: Record<any, any> | null;
  cells?: Array<RateTableModel> | null;
  key?: string | null;
  comment?: string | null;
  /**
   * Вердикт по полосам листа. Считает work-calc (`RateBandConflicts`), фронт только
   * красит: правило выбора полосы живёт в `PropertyRateLookupSql`, и повторять его
   * здесь нельзя.
   */
  bands?: {
    performance?: Array<BandVerdict> | null;
    quality?: Array<BandVerdict> | null;
  } | null;
  /** Проценты, не покрытые ни одной полосой: в закрытие уйдёт 0 без ошибки. */
  gaps?: Array<SheetGap> | null;
  severity?: RateSeverity | null;
}

export function normalizeSupplierId(id: unknown): number {
  if (id == null || id === '') {
    return 0;
  }
  if (typeof id === 'object' && id !== null && 'id' in (id as object)) {
    return normalizeSupplierId((id as { id: unknown }).id);
  }
  const parsed = parseInt(String(id), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** API rates return supplier ids as numbers; GraphQL may use strings — normalize for v-select. */
export function normalizeSupplierIds(suppliers: unknown[] | null | undefined): number[] {
  if (!suppliers?.length) {
    return [];
  }
  const ids = new Set<number>();
  suppliers.forEach((supplier) => {
    const id = normalizeSupplierId(supplier);
    if (id > 0) {
      ids.add(id);
    }
  });
  return [...ids].sort((a, b) => a - b);
}

export function normalizeSupplierFingerprint(suppliers: unknown[] | null | undefined): string {
  if (!suppliers?.length) {
    return '';
  }
  return normalizeSupplierIds(suppliers).join(',');
}

export function buildSupplierFingerprints(
  sheets: Array<SheetModel> | null | undefined,
): Record<string, number[]> {
  const map: Record<string, number[]> = {};
  (sheets || []).forEach((sheet, index) => {
    const fingerprint = normalizeSupplierFingerprint(sheet.suppliers as unknown[]);
    if (!map[fingerprint]) {
      map[fingerprint] = [];
    }
    map[fingerprint].push(index);
  });
  return map;
}

export function hasDuplicateSheets(sheets: Array<SheetModel> | null | undefined): boolean {
  return Object.values(buildSupplierFingerprints(sheets)).some((indices) => indices.length > 1);
}

export function isDuplicateSheet(
  sheet: SheetModel,
  sheets: Array<SheetModel> | null | undefined,
): boolean {
  const fingerprint = normalizeSupplierFingerprint(sheet.suppliers as unknown[]);
  const indices = buildSupplierFingerprints(sheets)[fingerprint];
  return !!indices && indices.length > 1;
}

/** Вердикт по цене: применяется она только там, где выигрывают ОБЕ её полосы. */
export interface CellVerdict {
  applies: 'full' | 'partial' | 'none';
  severity: RateSeverity;
  /** Цена сама отбирает процент у более узкой полосы — виновник, а не пострадавший. */
  conflict: boolean;
}

export interface RateTableModel {
  performance: {
    from: number | null;
    to: number | null;
    rate_id?: number | null;
  };
  quality: {
    from: number | null;
    to: number | null;
    rate_id?: number | null;
  };
  rate: number;
  tariff_id?: number | null;
  verdict?: CellVerdict | null;
}

export interface RateTableLocalModel {
  performance: string;
  quality: string;
  price: string;
}

export interface WorkTypeModel {
  tempId?: string | number;
  id: string | number;
  name: string;
  key: string;
  cost_center: Record<any, any> | null;
  CostAccount: Record<any, any> | null;
  work_billing_type: Record<any, any> | null;
  platform: Record<any, any> | null;
  client: Record<any, any> | null;
  rateTableCreated: boolean;
  rateTable?: Array<RateTableModel> | null;
  units: Record<any, any> | null;
  standard?: string;
  sheets?: Array<SheetModel> | null;
}

export interface WorkTypeCell {
  rateTable?: Array<RateTableModel> | null;
  cells?: Array<RateTableModel> | null;
  key?: any | null;
  comment?: string | null;
  bands?: SheetModel['bands'];
  gaps?: Array<SheetGap> | null;
  severity?: RateSeverity | null;
}

export const defaultTable: Array<RateTableModel> = [
  { rate: 0, quality: { from: 100, to: null }, performance: { from: 100, to: null } },
  { rate: 0, quality: { from: 0, to: 100 }, performance: { from: 100, to: null } },
  { rate: 0, quality: { from: 100, to: null }, performance: { from: 0, to: 100 } },
  { rate: 0, quality: { from: 0, to: 100 }, performance: { from: 0, to: 100 } },
];
export const defaultSingleSheet: SheetModel = {
  suppliers: [],
  cells: defaultTable,
  key: null,
};

export const defaultSheet: Array<SheetModel> = [
  defaultSingleSheet,
];

export const defaultTableListLocal: Array<RateTableLocalModel> = [
  {
    performance: '100',
    quality: '100',
    price: '0',
  },
  {
    performance: '95',
    quality: '100',
    price: '0',
  },
  {
    performance: '100',
    quality: '98',
    price: '0',
  },
  {
    performance: '95',
    quality: '98',
    price: '0',
  },
];

export const defaultTableList: Array<RateTableModel> = [
  {
    performance: {
      from: 100,
      to: null,
    },
    quality: {
      from: 100,
      to: null,
    },
    rate: 0,
  },
  {
    performance: {
      from: 95,
      to: 100,
    },
    quality: {
      from: 100,
      to: null,
    },
    rate: 0,
  },
  {
    performance: {
      from: 100,
      to: null,
    },
    quality: {
      from: 98,
      to: 100,
    },
    rate: 0,
  },
  {
    performance: {
      from: 95,
      to: 100,
    },
    quality: {
      from: 98,
      to: 100,
    },
    rate: 0,
  },
];

export const defaultWorkType: WorkTypeModel = {
  id: '',
  name: '',
  key: '',
  client: null,
  platform: null,
  cost_center: null,
  CostAccount: null,
  work_billing_type: null,
  rateTableCreated: false,
  units: null,
  standard: '',
  sheets: defaultSheet,
  rateTable: defaultTable,
};
